import React from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";

import { useDataStore } from "../dataStore";
import OptimizedConfigImage from "./OptimizedConfigImage";
import { DEFAULT_BRAND_THEME, brandThemeCssVars } from "../themeTokens";

export default function ClientAuthLayout({ aside, children }) {
  const {
    bannerUrl = "",
    logoUrl = "",
    brandName = "",
    brandTagline = "",
    brandTheme = DEFAULT_BRAND_THEME,
  } = useDataStore();
  const c = { ...DEFAULT_BRAND_THEME, ...brandTheme };

  return (
    <Box
      sx={{
        ...brandThemeCssVars(c),
        minHeight: { xs: "auto", md: "calc(100dvh - 190px)" },
        display: "grid",
        placeItems: "center",
        py: { xs: 1.5, sm: 3, md: 5 },
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 1080,
          overflow: "hidden",
          borderRadius: { xs: "20px", sm: "28px" },
          border: "1px solid rgba(67,20,7,0.10)",
          bgcolor: c.surface,
          boxShadow: "0 28px 90px rgba(33,20,12,0.14)",
        }}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            md={5}
            order={{ xs: 2, md: 1 }}
            sx={{ display: "flex" }}
          >
            <Box
              component="aside"
              sx={{
                width: "100%",
                minHeight: { xs: 230, md: 620 },
                position: "relative",
                isolation: "isolate",
                overflow: "hidden",
                p: { xs: 2.5, sm: 3.5, md: 4 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                color: "#fff",
                bgcolor: c.dark,
                borderTop: {
                  xs: "1px solid rgba(67,20,7,0.10)",
                  md: "none",
                },
                borderRight: {
                  xs: "none",
                  md: "1px solid rgba(67,20,7,0.10)",
                },
              }}
            >
              {bannerUrl ? (
                <Box
                  component={OptimizedConfigImage}
                  src={bannerUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: -3,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : null}

              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: -2,
                  background: bannerUrl
                    ? "linear-gradient(180deg, rgba(23,17,12,0.48) 0%, rgba(23,17,12,0.82) 72%, rgba(23,17,12,0.94) 100%)"
                    : `linear-gradient(145deg, ${c.dark} 0%, ${c.accent} 100%)`,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: -1,
                  background:
                    "radial-gradient(circle at 88% 12%, rgba(247,181,0,0.28), transparent 34%)",
                }}
              />

              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    overflow: "hidden",
                    borderRadius: "16px",
                    bgcolor: "rgba(255,255,255,0.92)",
                    border: "1px solid rgba(255,255,255,0.44)",
                    boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
                  }}
                >
                  {logoUrl ? (
                    <Box
                      component={OptimizedConfigImage}
                      src={logoUrl}
                      alt={brandName ? `${brandName} logo` : "Restaurant logo"}
                      sx={{
                        width: 42,
                        height: 42,
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <RestaurantRoundedIcon sx={{ color: c.accent }} />
                  )}
                </Box>
                {brandName || brandTagline ? (
                  <Box sx={{ minWidth: 0 }}>
                    {brandName ? (
                      <Typography
                        sx={{
                          color: "#fff",
                          fontWeight: 950,
                          lineHeight: 1.1,
                          fontSize: 18,
                        }}
                        noWrap
                      >
                        {brandName}
                      </Typography>
                    ) : null}
                    {brandTagline ? (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.35,
                          color: "rgba(255,255,255,0.76)",
                          lineHeight: 1.3,
                        }}
                        noWrap
                      >
                        {brandTagline}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
              </Stack>

              <Box
                sx={{
                  mt: { xs: 5, md: 8 },
                  p: { xs: 2.25, md: 2.5 },
                  borderRadius: "20px",
                  color: "#fff",
                  bgcolor: "rgba(23,17,12,0.46)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  backdropFilter: "blur(14px)",
                  "& .MuiTypography-root": {
                    color: "inherit",
                  },
                  "& .MuiTypography-colorTextSecondary": {
                    color: "rgba(255,255,255,0.74)",
                  },
                  "& .MuiButton-root": {
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.50)",
                    bgcolor: "rgba(255,255,255,0.08)",
                  },
                  "& .MuiButton-root:hover": {
                    borderColor: "#fff",
                    bgcolor: "rgba(255,255,255,0.16)",
                  },
                }}
              >
                {aside}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={7} order={{ xs: 1, md: 2 }}>
            <CardContent
              sx={{
                height: "100%",
                p: { xs: 2.5, sm: 4, md: 5.5 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                bgcolor: c.surface,
              }}
            >
              {children}
            </CardContent>
          </Grid>
        </Grid>
      </Card>
    </Box>
  );
}
